import React, { useState, useEffect, useMemo } from 'react';
// @ts-ignore
import SimpleMDE from 'react-simplemde-editor';
// @ts-ignore
import 'easymde/dist/easymde.min.css';
// @ts-ignore
import { marked } from 'marked';
// @ts-ignore
import DOMPurify from 'dompurify';
// @ts-ignore
import highlightjs from 'highlight.js';
// @ts-ignore
import 'highlight.js/styles/github.css';
// @ts-ignore
import TurndownService from 'turndown';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  errors?: string[];
  label?: string;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = 'ライター解説をマークダウン形式で入力してください',
  errors,
}) => {
  const [markdownValue, setMarkdownValue] = useState<string>('');
  
  // TurndownServiceをデフォルト設定で初期化
  const turndownService = useMemo(() => new TurndownService(), []);

  // markedの設定（コードハイライトと改行処理）
  useEffect(() => {
    // marked v11の設定
    // @ts-ignore - marked v11の型定義が不完全な場合がある
    marked.setOptions({
      // @ts-ignore
      highlight: (code: string, lang?: string) => {
        if (lang && highlightjs.getLanguage(lang)) {
          try {
            return highlightjs.highlight(code, { language: lang }).value;
          } catch (err) {
            return highlightjs.highlightAuto(code).value;
          }
        }
        return highlightjs.highlightAuto(code).value;
      },
      breaks: false, // 単一の改行を<br>に変換しない
      gfm: false, // GitHub Flavored Markdownを無効化（水平線の自動挿入を防ぐ）
    });
  }, []);

  // 外部からvalueが変更されたときに内部状態を更新
  useEffect(() => {
    if (!value) {
      setMarkdownValue('');
      return;
    }

    // HTMLの場合は、マークダウンに変換
    if (value.trim().startsWith('<')) {
      try {
        const markdown = turndownService.turndown(value);
        setMarkdownValue(markdown);
      } catch (error) {
        // 変換に失敗した場合は、HTMLタグを除去してテキストとして表示
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = value;
        setMarkdownValue(tempDiv.textContent || tempDiv.innerText || '');
      }
    } else {
      // 既にマークダウン形式の場合はそのまま
      setMarkdownValue(value);
    }
  }, [value, turndownService]);

  const handleChange = (val: string) => {
    setMarkdownValue(val);
    // マークダウンをHTMLに変換して保存（DOMPurifyでサニタイズ）
    const html = marked.parse(val) as string;
    const sanitizedHtml = DOMPurify.sanitize(html);
    onChange(sanitizedHtml);
  };

  // @ts-ignore - react-simplemde-editorの型定義が不完全な場合がある
  const options = useMemo(() => ({
    placeholder,
    spellChecker: false,
    status: ['lines', 'words', 'cursor'],
    toolbar: [
      'bold',
      'italic',
      'heading',
      '|',
      'quote',
      'unordered-list',
      'ordered-list',
      '|',
      'link',
      'image',
      'table',
      '|',
      'preview',
      'side-by-side',
      'fullscreen',
      '|',
      'guide'
    ],
    minHeight: '600px',
    autofocus: false,
    renderingConfig: {
      singleLineBreaks: false,
      codeSyntaxHighlighting: true,
    },
  }), [placeholder]);

  return (
    <div className="rich-text-editor-wrapper">
      <SimpleMDE
        value={markdownValue}
        onChange={handleChange}
        // @ts-ignore - react-simplemde-editorの型定義が不完全な場合がある
        options={options}
        className={errors && errors.length > 0 ? 'is-invalid' : ''}
      />
      {errors && errors.length > 0 && (
        <div className="invalid-feedback d-block mt-2">{errors.join(', ')}</div>
      )}
    </div>
  );
};

export default RichTextEditor;
